// 필요한 의존성 모듈들을 임포트
import React, { Suspense, useContext } from "react";
import { createBrowserRouter, Navigate, useLocation, useSearchParams } from "react-router-dom";

import { routes } from "@/app/constants";
import baseLayout from "@/app/layouts/baseLayout";
import FormProtectionProvider from "@/app/providers/FormProtectionProvider/FormProtectionProvider";
import ParamsProvider, { CompanyContext } from "@/app/providers/ParamsProvider/ParamsProvider";
import getAccountCodes from "@/shared/api/getAccountCodes";
import LoadingSpinner, { LoadingSpinnerProvider } from "@/shared/ui/LoadingSpinner";
import { clearRows } from "@/shared/ui/XadomTable/model/row/slice";
import appStore from "./appStore";

// 로딩 상태를 표시하는 폴백 컴포넌트
const LoadingFallback: React.FC = () => {
    return <LoadingSpinner message="잠시만 기다려주세요..." />;
};

// ContentEntity 페이지 이동을 감지하는 래퍼 컴포넌트
const ContentEntityWrapper: React.FC<{ component: React.ComponentType }> = ({ component: Component }) => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const prevPathRef = React.useRef<string>('');
    const companyInfo = useContext(CompanyContext);

    // 현재 경로에 해당하는 페이지 title 찾기
    const findPageTitle = (pathname: string) => {
        for (const pageDataGroup of Object.values(routes)) {
            for (const pageData of Object.values(pageDataGroup)) {
                if (pageData.path === pathname) {
                    return pageData.title;
                }
            }
        }
        return null;
    };

    // 페이지 타이틀 설정
    React.useEffect(() => {
        const pageTitle = findPageTitle(location.pathname);
        if (pageTitle) {
            document.title = `Xadom - ${pageTitle}`;
        } else {
            document.title = 'Xadom - 세무회계 자동화 솔루션';
        }
    }, [location.pathname]);

    const fetchAccountCodes = async () => {
        try {
            const cId = companyInfo?.company?.companyId;
            const period = companyInfo?.accountingPeriod?.period;

            if (!cId || !period) {
                console.log('필수 데이터 누락:', { companyId: cId, period });
                return;
            }

            if (location.pathname.includes('/OGAI')) {
                const response = await getAccountCodes({
                    cId,
                    period: Number(period),
                    actGrp: 'ALL'
                });

                // updateAccountList 함수가 있는 경우에만 업데이트
                if (companyInfo?.updateAccountList) {
                    companyInfo.updateAccountList(() => response.data);
                } else {
                    console.warn('updateAccountList 함수가 정의되지 않았습니다.');
                }
            }
        } catch (error) {
            console.error('계정 코드 조회 실패:', error);
        }
    };

    React.useEffect(() => {
        // 이전 경로와 같으면 중복 호출 방지
        if (prevPathRef.current === location.pathname) {
            return;
        }

        // ContentEntity 경로에 해당하는지 확인
        if (location.pathname.includes('/OGAI')) {
            // 현재 경로 저장
            prevPathRef.current = location.pathname;
            const ignoreList = ['/OGAI1000', '/OGAI2000', '/OGAI3000', '/OGAI4000', '/OGAI5000', '/OGAI6000', '/OGAI7000', '/OGAI8000', '/OGAI9000'];

            if (!ignoreList.includes(location.pathname)) {
                fetchAccountCodes();

                // XadomTable의 추가되어있는 모든 상태 삭제
                const state = appStore.getState();
                const dispatch = appStore.dispatch;
                
                // 모든 테이블의 행 데이터 삭제
                Object.keys(state.xadomTableRow).forEach(tableId => {
                    dispatch(clearRows({ tableId }));
                });

            }

        }
    }, [location.pathname]);

    return <Component />;
};

// 컴포넌트를 Suspense로 감싸는 고차 컴포넌트
// React.lazy()로 동적 임포트된 컴포넌트의 로딩 상태를 처리
const withSuspense = (Component: React.ComponentType) => (
    <Suspense fallback={<LoadingFallback />}>
        <ContentEntityWrapper component={Component} />

    </Suspense>
);

// routes 객체를 통해 라우트 설정을 동적으로 생성하는 함수
// routes 객체의 각 페이지 데이터 그룹을 순회하며 라우트 객체 배열 생성
const generateRoutes = () => {
    return Object.values(routes).flatMap(pageDataGroup =>
        Object.values(pageDataGroup).map(({ path, component }) => ({
            path,
            element: withSuspense(component),
            // 히스토리 관리를 위한 설정 추가
            index: false,
            caseSensitive: true,
            // 쿼리 파라미터 보존
            shouldRevalidate: () => false
        }))
    );
};

// 메인 라우터 설정
export const appRouter = createBrowserRouter([
    {
        // 전역 상태 관리를 위한 Provider들과 기본 레이아웃 설정
        element:
            <LoadingSpinnerProvider>
                <ParamsProvider>
                    <FormProtectionProvider>
                        {baseLayout}
                    </FormProtectionProvider>
                </ParamsProvider>
            </LoadingSpinnerProvider>,
        errorElement: <div>error</div>,
        children: [
            // 루트 경로를 /main으로 리다이렉트
            {
                path: "/",
                element: <Navigate to="/main" replace />
            },
            // 404 페이지 처리
            {
                path: "*",
                element: (<div>404</div>),
            },
            // routes 객체를 통해 생성된 동적 라우트들
            ...generateRoutes(),
        ],
    },
]);